/**
 * Page attachments: upload (button or drag and drop files), list,
 * download/open, and inline image preview.
 *
 * Binaries go to R2/MinIO via /api/storage/upload; the `attachment` row
 * (with page_id) travels through PowerSync, so the list works offline and is
 * shared with the page.
 *
 * PDF annotation is explicitly out of scope (Phase 2+): PDFs are
 * downloaded/opened with the system viewer.
 */
import { createEffect, createSignal, For, onCleanup, Show } from "solid-js";
import { Loader2, Paperclip, Upload } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import type { Attachment } from "@folio/powersync";
import {
  addPageAttachmentMutation,
  cn,
  deleteAttachmentMutation,
  MAX_ATTACHMENT_SIZE_BYTES,
} from "@folio/shared";
import { usePageAttachmentsQuery } from "@folio/shared/solid";
import { downloadStorageObject, fetchStorageBlobUrl, uploadStorageObject } from "@/lib/api";
import { showToast } from "@/components/ui/toast";
import { FileCard } from "@/features/notes/components/file-card";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";

function isImage(attachment: Attachment): boolean {
  return (attachment.content_type ?? "").startsWith("image/");
}

function ImagePreview(props: { attachment: Attachment }) {
  const [src, setSrc] = createSignal<string | null>(null);

  createEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const url = await fetchStorageBlobUrl(props.attachment.url);
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setSrc(url);
      } catch (err: unknown) {
        console.warn("[notes] Image preview failed:", err);
      }
    })();
    onCleanup(() => {
      revoked = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    });
  });

  return (
    <Show when={src()}>
      {(url) => (
        <img
          src={url()}
          alt={props.attachment.filename ?? ""}
          class="max-h-72 rounded-lg border border-border/40 object-contain"
          loading="lazy"
        />
      )}
    </Show>
  );
}

interface PageAttachmentsProps {
  pageId: string;
  // Expanded preview addressable by URL (`/documents/$pageId?attachment=<id>`).
  // If the parent controls it (signals derived from the query), we use it as
  // the source of truth; otherwise we fall back to a local signal (reusable
  // component).
  expandedId?: string | null;
  onExpandedChange?: (id: string | null) => void;
}

export function PageAttachments(props: PageAttachmentsProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();

  const [isUploading, setIsUploading] = createSignal(false);
  const [isDragOver, setIsDragOver] = createSignal(false);
  const [localExpandedId, setLocalExpandedId] = createSignal<string | null>(null);
  const isControlled = () => props.onExpandedChange !== undefined;
  const expandedId = () => (isControlled() ? (props.expandedId ?? null) : localExpandedId());
  const setExpandedId = (id: string | null) => {
    if (isControlled()) {
      props.onExpandedChange?.(id);
    } else {
      setLocalExpandedId(id);
    }
  };
  let fileInput: HTMLInputElement | undefined;

  const attachments = usePageAttachmentsQuery({
    db: () => db(),
    drizzle,
    pageId: () => props.pageId,
  });

  const uploadFile = async (file: File) => {
    const dbInstance = drizzle();
    const uid = userId();
    if (!dbInstance || !uid || isUploading()) {
      return;
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      showToast(t(msg`File is too large.`), "error");
      return;
    }
    setIsUploading(true);
    try {
      const { url } = await uploadStorageObject(file);
      await addPageAttachmentMutation({
        db: dbInstance,
        pageId: props.pageId,
        userId: uid,
        filename: file.name,
        url,
        contentType: file.type || "application/octet-stream",
        size: file.size,
      });
    } catch (err) {
      console.error("Error uploading attachment:", err);
      showToast(t(msg`Failed to upload attachment. Please try again.`), "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (attachment: Attachment) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await deleteAttachmentMutation(dbInstance, attachment.id);
    } catch (err) {
      console.error("Error deleting attachment:", err);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      await downloadStorageObject(attachment.url, attachment.filename ?? "archivo");
    } catch (err) {
      console.error("Error downloading attachment:", err);
      showToast(t(msg`Failed to download attachment.`), "error");
    }
  };

  return (
    <section
      class={cn(
        "mt-10 rounded-xl border border-dashed border-border/40 p-4 transition-colors",
        isDragOver() && "border-accent/60 bg-accent/5",
      )}
      onDragOver={(event) => {
        if (event.dataTransfer?.types.includes("Files")) {
          event.preventDefault();
          setIsDragOver(true);
        }
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragOver(false);
        const files = event.dataTransfer?.files;
        if (files) {
          for (const file of files) {
            void uploadFile(file);
          }
        }
      }}
    >
      <div class="flex items-center justify-between gap-2">
        <h3 class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">
          <Paperclip class="h-3.5 w-3.5" />
          {t(msg`Attachments`)}
          <Show when={(attachments.data?.length ?? 0) > 0}>
            <span class="opacity-60">({attachments.data?.length})</span>
          </Show>
        </h3>
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-muted-foreground/80 hover:bg-accent/10 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => fileInput?.click()}
          disabled={isUploading() || !drizzle()}
        >
          <Show when={!isUploading()} fallback={<Loader2 class="h-3.5 w-3.5 animate-spin" />}>
            <Upload class="h-3.5 w-3.5" />
          </Show>
          {t(msg`Upload file`)}
        </button>
        <input
          ref={fileInput}
          type="file"
          multiple
          class="hidden"
          // Office editors mount their own hidden input on the same page (e.g.
          // `docx-import-input`): without a dedicated testid, a bare
          // `input[type="file"]` resolves to several nodes.
          data-testid="attachment-upload-input"
          onChange={(event) => {
            const { files } = event.currentTarget;
            if (files) {
              for (const file of files) {
                void uploadFile(file);
              }
            }
            event.currentTarget.value = "";
          }}
        />
      </div>

      <Show
        when={(attachments.data?.length ?? 0) > 0}
        fallback={
          <p class="pt-3 text-xs text-muted-foreground/50">
            {t(msg`Drop files here or use the upload button.`)}
          </p>
        }
      >
        <ul class="mt-3 space-y-2">
          <For each={attachments.data ?? []}>
            {(attachment) => (
              <li class="space-y-2">
                <FileCard
                  name={attachment.filename ?? t(msg`Untitled`)}
                  size={attachment.size}
                  contentType={attachment.content_type}
                  uploadedAt={attachment.created_at}
                  isExpanded={expandedId() === attachment.id}
                  onToggleExpand={() =>
                    setExpandedId(expandedId() === attachment.id ? null : attachment.id)
                  }
                  onDownload={() => {
                    void handleDownload(attachment);
                  }}
                  onDelete={() => {
                    void handleDelete(attachment);
                  }}
                />
                <Show when={expandedId() === attachment.id && isImage(attachment)}>
                  <ImagePreview attachment={attachment} />
                </Show>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </section>
  );
}
